import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpProgressEvent, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface UploadFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'cancelled';
  error?: string;
  targetPath: string;
  bucketName: string;
  startTime?: Date;
  endTime?: Date;
}

export interface UploadSession {
  id: string;
  files: UploadFile[];
  overallProgress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'cancelled';
  startTime: Date;
  endTime?: Date;
  targetPath: string;
  bucketName: string;
}

@Injectable({
  providedIn: 'root'
})
export class UploadProgressService {
  private apiBaseUrl = environment.apiBaseUrl;
  
  // Current upload session
  private currentSessionSubject = new BehaviorSubject<UploadSession | null>(null);
  public currentSession$ = this.currentSessionSubject.asObservable();
  
  // Minimized state
  private isMinimizedSubject = new BehaviorSubject<boolean>(false);
  public isMinimized$ = this.isMinimizedSubject.asObservable();

  // Cancel tokens for individual uploads
  private cancelTokens = new Map<string, Subject<void>>();

  constructor(private http: HttpClient) {}

  /**
   * Start a new upload session with multiple files
   */
  startUploadSession(files: File[], targetPath: string, bucketName: string = 'questions-images-bucket'): Observable<UploadSession> {
    const sessionId = this.generateId();
    const uploadFiles: UploadFile[] = files.map(file => ({
      id: this.generateId(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      progress: 0,
      status: 'pending' as const,
      targetPath: targetPath,
      bucketName: bucketName
    }));

    const session: UploadSession = {
      id: sessionId,
      files: uploadFiles,
      overallProgress: 0,
      status: 'uploading',
      startTime: new Date(),
      targetPath: targetPath,
      bucketName: bucketName
    };

    this.currentSessionSubject.next(session);
    this.isMinimizedSubject.next(false); // Expand popup when new upload starts

    // Start uploading files in parallel (max 3 concurrent uploads)
    this.processUploads(session);

    return of(session);
  }

  /**
   * Process uploads with concurrency control
   */
  private async processUploads(session: UploadSession): Promise<void> {
    const concurrentLimit = 3;
    const pendingFiles = session.files.filter(f => f.status === 'pending');
    
    // Process files in batches
    for (let i = 0; i < pendingFiles.length; i += concurrentLimit) {
      const batch = pendingFiles.slice(i, i + concurrentLimit);
      
      const uploadPromises = batch.map(file => 
        this.uploadSingleFile(file, session).toPromise()
      );
      
      await Promise.all(uploadPromises);
      
      // Check if session was cancelled
      const currentSession = this.currentSessionSubject.getValue();
      if (!currentSession || currentSession.status === 'cancelled') {
        break;
      }
    }

    // Update final session status
    this.updateSessionStatus();
  }

  /**
   * Upload a single file with progress tracking
   */
  private uploadSingleFile(uploadFile: UploadFile, session: UploadSession): Observable<void> {
    const cancelToken = new Subject<void>();
    this.cancelTokens.set(uploadFile.id, cancelToken);

    // Update file status to uploading
    uploadFile.status = 'uploading';
    uploadFile.startTime = new Date();
    this.updateSession();

    const formData = new FormData();
    formData.append('files', uploadFile.file);
    formData.append('targetFolder', uploadFile.targetPath);
    formData.append('bucketName', uploadFile.bucketName);

    const request = new HttpRequest('POST', `${this.apiBaseUrl}/api/upload/files`, formData, {
      reportProgress: true
    });

    return new Observable<void>(observer => {
      const subscription = this.http.request(request).subscribe({
        next: (event: HttpEvent<any>) => {
          if (event.type === HttpEventType.UploadProgress) {
            const progressEvent = event as HttpProgressEvent;
            if (progressEvent.total) {
              uploadFile.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              this.updateOverallProgress();
            }
          } else if (event.type === HttpEventType.Response) {
            uploadFile.status = 'completed';
            uploadFile.progress = 100;
            uploadFile.endTime = new Date();
            this.updateSession();
            observer.next();
            observer.complete();
          }
        },
        error: (error) => {
          uploadFile.status = 'failed';
          uploadFile.error = error.message || 'Upload failed';
          uploadFile.endTime = new Date();
          this.updateSession();
          observer.next(); // Don't error out the observable
          observer.complete();
        }
      });

      // Handle cancellation
      cancelToken.subscribe(() => {
        subscription.unsubscribe();
        uploadFile.status = 'cancelled';
        uploadFile.endTime = new Date();
        this.updateSession();
        observer.next();
        observer.complete();
      });
    });
  }

  /**
   * Update overall progress based on all files
   */
  private updateOverallProgress(): void {
    const session = this.currentSessionSubject.getValue();
    if (!session) return;

    const totalProgress = session.files.reduce((sum, file) => sum + file.progress, 0);
    session.overallProgress = Math.round(totalProgress / session.files.length);
    this.currentSessionSubject.next({ ...session });
  }

  /**
   * Update session status based on file statuses
   */
  private updateSessionStatus(): void {
    const session = this.currentSessionSubject.getValue();
    if (!session) return;

    const allCompleted = session.files.every(f => f.status === 'completed');
    const anyFailed = session.files.some(f => f.status === 'failed');
    const allDone = session.files.every(f => 
      f.status === 'completed' || f.status === 'failed' || f.status === 'cancelled'
    );

    if (allDone) {
      if (allCompleted) {
        session.status = 'completed';
      } else if (anyFailed) {
        session.status = 'failed';
      }
      session.endTime = new Date();
    }

    this.currentSessionSubject.next({ ...session });
  }

  /**
   * Update session in subject
   */
  private updateSession(): void {
    const session = this.currentSessionSubject.getValue();
    if (session) {
      this.updateOverallProgress();
      this.currentSessionSubject.next({ ...session });
    }
  }

  /**
   * Cancel a single file upload
   */
  cancelFileUpload(fileId: string): void {
    const cancelToken = this.cancelTokens.get(fileId);
    if (cancelToken) {
      cancelToken.next();
      cancelToken.complete();
      this.cancelTokens.delete(fileId);
    }

    const session = this.currentSessionSubject.getValue();
    if (session) {
      const file = session.files.find(f => f.id === fileId);
      if (file && file.status === 'pending') {
        file.status = 'cancelled';
        this.updateSession();
      }
    }
  }

  /**
   * Cancel all uploads
   */
  cancelAllUploads(): void {
    this.cancelTokens.forEach((token, id) => {
      token.next();
      token.complete();
    });
    this.cancelTokens.clear();

    const session = this.currentSessionSubject.getValue();
    if (session) {
      session.files.forEach(file => {
        if (file.status === 'pending' || file.status === 'uploading') {
          file.status = 'cancelled';
        }
      });
      session.status = 'cancelled';
      session.endTime = new Date();
      this.currentSessionSubject.next({ ...session });
    }
  }

  /**
   * Retry a failed file upload
   */
  retryFileUpload(fileId: string): void {
    const session = this.currentSessionSubject.getValue();
    if (!session) return;

    const file = session.files.find(f => f.id === fileId);
    if (file && file.status === 'failed') {
      file.status = 'pending';
      file.progress = 0;
      file.error = undefined;
      session.status = 'uploading';
      this.currentSessionSubject.next({ ...session });
      
      // Re-process the single file
      this.uploadSingleFile(file, session).subscribe(() => {
        this.updateSessionStatus();
      });
    }
  }

  /**
   * Retry all failed uploads
   */
  retryAllFailed(): void {
    const session = this.currentSessionSubject.getValue();
    if (!session) return;

    const failedFiles = session.files.filter(f => f.status === 'failed');
    failedFiles.forEach(file => {
      file.status = 'pending';
      file.progress = 0;
      file.error = undefined;
    });

    if (failedFiles.length > 0) {
      session.status = 'uploading';
      this.currentSessionSubject.next({ ...session });
      this.processUploads(session);
    }
  }

  /**
   * Clear completed files from the list
   */
  clearCompletedFiles(): void {
    const session = this.currentSessionSubject.getValue();
    if (!session) return;

    session.files = session.files.filter(f => f.status !== 'completed');
    
    if (session.files.length === 0) {
      this.clearSession();
    } else {
      this.currentSessionSubject.next({ ...session });
    }
  }

  /**
   * Clear the current session
   */
  clearSession(): void {
    this.cancelTokens.clear();
    this.currentSessionSubject.next(null);
  }

  /**
   * Set minimized state
   */
  setMinimized(minimized: boolean): void {
    this.isMinimizedSubject.next(minimized);
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current session
   */
  getCurrentSession(): UploadSession | null {
    return this.currentSessionSubject.getValue();
  }

  /**
   * Check if there's an active upload
   */
  hasActiveUpload(): boolean {
    const session = this.currentSessionSubject.getValue();
    return session !== null && session.status === 'uploading';
  }
}
